import Icon from '@ant-design/icons/lib/components/Icon';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useElements } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import { Button, Col, Input, InputNumber, Modal, Row, Select } from 'antd';
import Form, { FormInstance, Rule } from 'antd/lib/form';
import cx from 'classnames';
import { useDispatch } from 'react-redux';

import { ReactComponent as ChevronIconSVG } from '../../../../assets/sightx-icons/svg/icon-chevron.svg';
import { ReactComponent as CrossSVG } from '../../../../assets/sightx-icons/svg/x-close.svg';
import { IAccount } from '../../../../slices/accounts/account';
import { createPaymentAndBilling } from '../../../../slices/accounts/paymentAndBilling/paymentAndBilling.thunk';
import { showErrorMessage } from '../../../../slices/notifications';
import { AccessTypes } from '../../../common/domain/accountAccessType';
import { IOption } from '../../../invoices/invoiceDealSelectionModal';
import { Address } from '../../modal/paymentAndBilling/address';
import { PaymentOptionsForSelect } from '../../modal/paymentAndBilling/paymentOptions';
import { PaymentOptions } from '../../modal/paymentAndBilling/types';
import { InvoiceTermsOptionsSelect } from '../utils';
import { SxEmailTags } from '../../../common/SxEmailTags';
import { Features, isFeatureEnabled } from '../../../../featureToggles/features';

import styles from './paymentForm.module.scss';

interface PaymentFormProps {
  account: IAccount;
  setFormComplete: (complete: boolean) => void;
  formRef: React.RefObject<FormInstance<IFormValues>>;
  setHasAdressChanged: (state: boolean) => void;
}

export interface IFormValues {
  email: string;
  invoiceTerms?: string;
  priceOfNewLicenses?: number;
  paymentOptions: Array<string>;
  sampleDiscountPercentage: number;
  rateCardUd?: string;
}

const MIN_LICENSE_PRICE = 500;
const MAX_LICENSE_PRICE = 10000;

export const PaymentForm = (props: PaymentFormProps): ReactElement => {
  const dispatch = useDispatch();
  const elements = useElements();
  const [isInvoiceSelected, setIsInvoiceSelected] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [invoiceNotes, setInvoiceNotes] = useState<string>();
  const [invoicedEmails, setInvoicedEmails] = useState<string[]>([]);
  const [isSubmittingFromModal, setSubmittingFromModal] = useState(false);
  const changesCount = useRef(0);
  const {
    account: {
      sampleDiscountPercentage,
      billingAddress = null,
      priceOfNewLicenses,
      invoiceTerms,
      accessType,
      renewalDate = 'To be defined',
      currentDeal,
      rateCardUd
    },
    setFormComplete,
    formRef,
    setHasAdressChanged
  } = props;

  let paymentOptions = useMemo(() => props.account.paymentOptions || [], [props.account.paymentOptions]);

  const { email = '' } = billingAddress || {};
  const [form] = Form.useForm();
  const addressElement = elements && elements.getElement('address');

  const isValidForm = useCallback((formValues?: IFormValues) => {
    if (!formValues) {
      return false;
    }
    const { email = '', invoiceTerms = '', priceOfNewLicenses, paymentOptions, sampleDiscountPercentage } = formValues;
    setInvoicedEmails([email]);
    const emptyEmail = !email || email.trim() === '';
    const emptyInvoiceTerms = !invoiceTerms || invoiceTerms.trim() === '';
    const emptyPrice = priceOfNewLicenses === undefined || priceOfNewLicenses === null;
    // TODO: confirm if is OK to skip validation of emptyDiscount:
    const emptyDiscount = isFeatureEnabled(Features.DeskIntegration) ? false : (sampleDiscountPercentage === undefined || sampleDiscountPercentage === null);

    let missingInvoiceTermsDueInvoice = false;
    if(paymentOptions.includes(PaymentOptions.Invoice) && emptyInvoiceTerms) {
      missingInvoiceTermsDueInvoice = true;
    }

    let areMissingFields = false;
    switch (accessType) {
    case AccessTypes.Lite:
      areMissingFields = emptyEmail || emptyPrice || emptyDiscount;
      break;
    case AccessTypes.Enterprise:
    case AccessTypes.Professional:
      areMissingFields = emptyEmail || missingInvoiceTermsDueInvoice || emptyPrice || emptyDiscount;
      break;
    }

    return !areMissingFields;
  }, [accessType]);

  useEffect(() => {
    const setAddressComplete = async () => {
      const addressComplete = addressElement && (await addressElement.getValue()).complete;
      const formValid = isValidForm(formRef.current?.getFieldsValue());
      setFormComplete(!!addressComplete && formValid);
    };
    setAddressComplete();
    setIsInvoiceSelected(paymentOptions.includes(PaymentOptions.Invoice));
  }, [addressElement, paymentOptions, formRef, setFormComplete, isValidForm]);

  const onChangeAddress = (event: StripeAddressElementChangeEvent) => {
    const formValid = isValidForm(formRef.current?.getFieldsValue());
    changesCount.current += 1;
    if(changesCount.current > 1) { // This functions gets trigger even on mount. that's why the > 1
      setHasAdressChanged(true);
    }
    setFormComplete(event.complete && formValid);
  };

  const handleSetInvoicedEmails = (value: string[]) => {
    setInvoicedEmails(value);
  };

  const isEnterpriseAccount = accessType === AccessTypes.Enterprise;
  const hasDealAmountEqualsZero = currentDeal?.amount === 0;

  const savePayment = async (formValues: IFormValues) => {
    const currentSampleDiscountPercentageIsNumber = typeof props.account.sampleDiscountPercentage === 'number';
    const {
      paymentOptions = [],
      sampleDiscountPercentage = (isFeatureEnabled(Features.DeskIntegration) && currentSampleDiscountPercentageIsNumber) ? props.account.sampleDiscountPercentage : 0,
      email,
      invoiceTerms,
      priceOfNewLicenses,
      rateCardUd
    } = formValues;
    const { account } = props;
    const { id: accountId } = account;
    if (!elements) {
      return;
    }

    if (paymentOptions.includes(PaymentOptions.Invoice) && !formValues.invoiceTerms) {
      dispatch(showErrorMessage('Please select an invoice term from the select list'));
      return;
    }

    const addressElement = elements.getElement('address');
    if (!addressElement) {
      return;
    }
    const { complete, value } = await addressElement.getValue();

    if (!complete) {
      return;
    }

    const { firstName, lastName, phone, address } = value;

    let sendInvoice = !currentDeal?.invoiced;
    if (!isEnterpriseAccount) {
      sendInvoice = false;
    }

    const recipientEmails = invoicedEmails.filter((recipientEmail) => recipientEmail !== email);
    const carbonCopyRecipients = recipientEmails.length ? recipientEmails : undefined;

    setSubmittingFromModal(true);
    dispatch(createPaymentAndBilling({
      accountId,
      paymentOptions,
      sampleDiscountPercentage,
      rateCardUd,
      invoiceTerms,
      priceOfNewLicenses,
      dealAmount: currentDeal?.amount ?? 0,
      billingAddress: {
        firstName,
        lastName,
        phone,
        address,
        email
      },
      purchaseOrderNumber: invoiceNotes,
      sendInvoice,
      carbonCopyRecipients
    }));
    changesCount.current = 0;
    setHasAdressChanged(false);
  };

  const handleSubmit = (formValues: IFormValues) => {
    const hasInvoicedDeal = currentDeal?.invoiced;
    if (accessType !== AccessTypes.Enterprise || hasInvoicedDeal || hasDealAmountEqualsZero) {
      savePayment(formValues);
      return;
    }
    setIsModalOpen(true);
  };

  const handleSubmitFromModal = () => {
    closeModal();
    const formValues = formRef.current?.getFieldsValue();
    if (!formValues) {
      return;
    }
    savePayment(formValues);
  };

  const handleFormChange = async (changedValues: IFormValues, allValues: IFormValues) => {
    const addressComplete = addressElement && (await addressElement.getValue()).complete;
    const formValid = isValidForm(allValues);
    setFormComplete(!!addressComplete && formValid);
  };

  if (isEnterpriseAccount) {
    [PaymentOptions.ServiceProject, PaymentOptions.Invoice].forEach(option => {
      if (!paymentOptions.includes(option)) {
        paymentOptions = [...paymentOptions, option];
      }
    });
  }

  const initialValues: IFormValues = {
    paymentOptions,
    sampleDiscountPercentage,
    email,
    priceOfNewLicenses: !isEnterpriseAccount ? MAX_LICENSE_PRICE : priceOfNewLicenses,
    invoiceTerms,
    rateCardUd
  };

  const validateMinAdMax = useCallback(
    ({ getFieldValue }) => ({
      validator(rule: Rule, _: number) {
        if (MIN_LICENSE_PRICE > getFieldValue('priceOfNewLicenses')) {
          return Promise.reject('License price cannot be less than $500');
        }
        if (MAX_LICENSE_PRICE < getFieldValue('priceOfNewLicenses')) {
          return Promise.reject('License price cannot be more than $10000');
        }
        return Promise.resolve();
      }
    }),
    []
  );

  const onChangePaymentMethod = (values: string[]) => {
    const isInvoiceSelected = values.includes(PaymentOptions.Invoice);
    setIsInvoiceSelected(isInvoiceSelected);
  };

  const renderPaymentConfigurations = () =>
    <div className={cx(styles.container, styles.expandWidth)}>
      <div className={styles.subHeaderConfiguration}>
        <p className={styles.title}>Payment Configurations</p>
        {/* TODO: remove hidden when renewalDate comes in props */}
        <div className={styles.chip} hidden>Renewal date <span>{renewalDate}</span></div>
      </div>
      <Row gutter={[18, 18]} wrap={false}>
        <Col flex={isFeatureEnabled(Features.DeskIntegration) ? undefined : 5} span={isFeatureEnabled(Features.DeskIntegration) ? 12 : undefined}>
          <Form.Item name='paymentOptions' label='Sample payment methods'>
            <Select
              mode='multiple'
              suffixIcon={<Icon component={ChevronIconSVG} />}
              showArrow
              allowClear
              placeholder='Please select'
              options={getPaymentOptions()}
              onChange={onChangePaymentMethod}
            />
          </Form.Item>
        </Col>
        {
          isFeatureEnabled(Features.DeskIntegration)
            ?
            (
              <Col span={12}>
                <Form.Item
                  name='rateCardUd'
                  label='Desk sample rate card UD'
                  rules={[{ type: 'string', min: 1 }]}
                >
                  <Input placeholder='Desk rate card UD' />
                </Form.Item>
              </Col>
            )
            :
            (
              <Col flex={2}>
                <Form.Item
                  name="sampleDiscountPercentage"
                  label="Sample discount (%)"
                  rules={[{ required: true }]}
                >
                  {/* Confirm defaultValue if needs to be optional */}
                  <InputNumber className={styles.sampleDiscountInput} min={0} max={100} defaultValue={isFeatureEnabled(Features.DeskIntegration) ? 0 : undefined} value={0}/>
                </Form.Item>
              </Col>
            )
        }
      </Row>
      <Row gutter={[24, 24]}>
        {isEnterpriseAccount &&
          <Col flex={ isFeatureEnabled(Features.DeskIntegration) ? undefined : 2 } span={isFeatureEnabled(Features.DeskIntegration) ? 12 : undefined }>
            <Form.Item
              name="priceOfNewLicenses"
              label="Price of new licenses ($)"
              rules={[{ required: true }, validateMinAdMax]}
            >
              <InputNumber
                className={styles.sampleDiscountInput}
                placeholder='3000'
              />
            </Form.Item>
          </Col>
        }
        {!isEnterpriseAccount &&
          <Form.Item
            name="priceOfNewLicenses"
            label="Price of new licenses ($)"
            hidden
          >
            <InputNumber
              className={styles.sampleDiscountInput}
              placeholder='3000'
            />
          </Form.Item>
        }
        <Col flex={isFeatureEnabled(Features.DeskIntegration) ? undefined : 2} span={isFeatureEnabled(Features.DeskIntegration) ? 12 : undefined}>
          <Form.Item name='invoiceTerms' label='Invoice terms' required={isInvoiceSelected}>
            <Select
              suffixIcon={<Icon component={ChevronIconSVG} />}
              showArrow
              allowClear
              placeholder='Please select'
              options={[...InvoiceTermsOptionsSelect]}
              value={invoiceTerms}
            />
          </Form.Item>
        </Col>
      </Row>
    </div>;

  const getPaymentOptions = (): IOption[] => {
    const { accessType } = props.account;
    if ([AccessTypes.Lite, AccessTypes.Professional].includes(accessType)) {
      return [...PaymentOptionsForSelect].map(option => ({
        ...option,
        disabled: option.value !== PaymentOptions.CreditCard
      }));
    }

    if (accessType === AccessTypes.Enterprise) {
      return [...PaymentOptionsForSelect].map(option => ({
        ...option,
        disabled: [PaymentOptions.Invoice, PaymentOptions.ServiceProject].includes(option.value)
      }));
    }

    return [...PaymentOptionsForSelect];
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSubmittingFromModal(false);
  };
  const changeInvoiceNotes = (e: React.ChangeEvent<HTMLInputElement>) => setInvoiceNotes(e.target.value);

  const renderModal = (): ReactElement => {
    return <Modal
      visible={isModalOpen}
      className={styles.modal}
      onCancel={closeModal}
      title={<div className={styles.title}>Invoice details</div>}
      centered
      closable
      maskClosable
      width={450}
      closeIcon={<Icon component={CrossSVG} />}
      footer={null}
    >
      <>
        <div>PO # / Invoice notes</div>
        <Input placeholder="Add optional PO or note for invoice" onChange={changeInvoiceNotes} disabled={isSubmittingFromModal}/>
        <div className={styles.invoicedEmails}>
          <label>Send invoice to</label>
          <SxEmailTags
            emails={invoicedEmails}
            setEmails={handleSetInvoicedEmails}
            maxAllowed={10}
            disabled={isSubmittingFromModal}
            withReadOnly
          />
        </div>
        {renderFooter()}
      </>
    </Modal>;
  };

  const renderFooter = () => {
    const primaryButtonText = hasDealAmountEqualsZero ? 'Send' : 'Send invoice';
    return (
      <div className={styles.footer}>
        <Button className={styles.footerButton} onClick={closeModal} >Cancel</Button>
        <Button className={styles.footerButton} onClick={handleSubmitFromModal} type='primary'>{primaryButtonText}</Button>
      </div>
    );
  };

  return (<>
    <Form
      onFinish={handleSubmit}
      validateMessages={{ required: 'Required' }}
      form={form}
      initialValues={initialValues}
      layout='vertical'
      ref={formRef}
      onValuesChange={handleFormChange}
      className={cx(styles.form, { [styles.withWarning]: !billingAddress })}
    >
      {renderPaymentConfigurations()}
      <div className={styles.billingContainer}>
        <div className={styles.container}>
          <p className={styles.title}>Billing Details</p>
          <section className={styles.addressSection}>
            <div className={styles.addressContainer}>
              <Address initialValues={billingAddress} onChange={onChangeAddress} name={props.account.name} />
            </div>
          </section>
        </div>
      </div>
    </Form>
    {renderModal()}
  </>
  );
};
